import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TalentService } from '../talent.service';
import { TestAssignmentService } from '../test-assignment.service';
import { MatDialog } from '@angular/material/dialog';
import { CreateAssignmentDialogComponent } from './create-assignment-dialog.component';

@Component({
    selector: 'app-ai-test-detail',
    templateUrl: './ai-test-detail.component.html',
    styleUrls: ['./ai-test-detail.component.css'],
    standalone: false
})
export class AiTestDetailComponent implements OnInit {
  testId: number;
  test: any = null;
  assignments: any = [];
  
  isLoading: boolean = false;
  isLoadingAssignments: boolean = false;
  errorMessage: string = '';
  successMessage: string = '';

  expandedQuestions: Set<number> = new Set();

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private talentService: TalentService,
    private testAssignmentService: TestAssignmentService,
    private dialog: MatDialog
  ) {}

  ngOnInit() {
    this.route.params.subscribe(params => {
      this.testId = +params['id'];
      this.loadTestDetail();
      this.loadAssignments();
    });
  }

  loadTestDetail() {
    this.isLoading = true;
    this.errorMessage = '';

    this.talentService.getCandidateTestDetail(this.testId).subscribe(
      (test) => {
        this.test = test;
        this.isLoading = false;
      },
      (error) => {
        console.error('Error loading test detail:', error);
        this.errorMessage = 'Failed to load test details';
        this.isLoading = false;
      }
    );
  }

  loadAssignments() {
    this.isLoadingAssignments = true;

    this.talentService.getTestAssignments(this.testId).subscribe(
      (assignments) => {
        this.assignments = assignments;
        this.isLoadingAssignments = false;
      },
      (error) => {
        console.error('Error loading assignments:', error);
        this.isLoadingAssignments = false;
      }
    );
  }

  toggleQuestion(index: number) {
    if (this.expandedQuestions.has(index)) {
      this.expandedQuestions.delete(index);
    } else {
      this.expandedQuestions.add(index);
    }
  }

  isQuestionExpanded(index: number): boolean {
    return this.expandedQuestions.has(index);
  }

  createAssignment() {
    const dialogRef = this.dialog.open(CreateAssignmentDialogComponent, {
      width: '500px',
      data: { 
        testId: this.testId,
        testName: this.test && this.test.name ? this.test.name : `Test #${this.testId}`
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.successMessage = 'Assignment created successfully!';
        this.loadAssignments();
        setTimeout(() => {
          this.successMessage = '';
        }, 3000);
      }
    });
  }

  viewCandidate(candidateId: number) {
    this.router.navigate(['/candidate-detail', candidateId]);
  }

  getTestLink(code: string): string {
    return this.testAssignmentService.getTestLink(code);
  }

  copyTestLink(code: string): void {
    this.testAssignmentService.copyTestLink(code);
  }

  viewAssignment(assignmentId: number): void {
    this.router.navigate(['/test-assignment-detail', assignmentId]);
  }

  getStatusBadgeClass(status: string): string {
    return this.testAssignmentService.getStatusBadgeClass(status);
  }

  getStatusDisplay(status: string): string {
    return this.testAssignmentService.getStatusDisplay(status);
  }

  formatDate(dateString: string): string {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
  }

  goBack() {
    this.router.navigate(['/ai-test-list']);
  }

  archiveTest() {
    const isArchived = this.test && this.test.status === 'archived';
    const action = isArchived ? 'unarchive' : 'archive';

    this.talentService.archiveCandidateTest(this.testId).subscribe(
      (response) => {
        if (response.status === 'success') {
          this.successMessage = response.message;
          this.loadTestDetail();
        } else {
          this.errorMessage = response.message || `Failed to ${action} test`;
        }
      },
      (error) => {
        console.error(`Error ${action}ing test:`, error);
        this.errorMessage = `Failed to ${action} test`;
      }
    );
  }

  deleteTest() {
    if (this.assignments.length > 0) {
      alert('Cannot delete test with existing assignments');
      return;
    }

    if (!confirm('Are you sure you want to delete this test? This action cannot be undone.')) {
      return;
    }

    this.talentService.deleteCandidateTest(this.testId).subscribe(
      (response) => {
        if (response.status === 'success') {
          this.router.navigate(['/ai-test-list']);
        } else {
          alert(response.message || 'Failed to delete test');
        }
      },
      (error) => {
        console.error('Error deleting test:', error);
        alert('Failed to delete test');
      }
    );
  }

  getTestTypeDisplay(type: string): string {
    return type === 'multiple_choice' ? 'Multiple Choice' : 'Free Form Coding';
  }

  getOptionLetter(index: number): string {
    return String.fromCharCode(65 + index);
  }

  getDifficultyDisplay(difficulty: string): string {
    if (!difficulty) return 'N/A';
    return difficulty.charAt(0).toUpperCase() + difficulty.slice(1);
  }

  getDifficultyBadgeClass(difficulty: string): string {
    switch(difficulty) {
      case 'basic': return 'badge-success';
      case 'intermediate': return 'badge-warning';
      case 'advanced': return 'badge-danger';
      default: return 'badge-secondary';
    }
  }

  editTest() {
    if (this.test.assignment_count > 0) {
      alert('Cannot edit test with existing assignments');
      return;
    }
    this.router.navigate(['/ai-test-generation'], { 
      queryParams: { edit: true, testId: this.testId } 
    });
  }

  cloneTest() {
    if (confirm('Create a copy of this test?')) {
      this.talentService.cloneCandidateTest(this.testId).subscribe(
        (response) => {
          if (response.status === 'success') {
            this.successMessage = 'Test cloned successfully!';
            setTimeout(() => {
              this.router.navigate(['/ai-test-detail', response.test.id]);
            }, 1000);
          } else {
            this.errorMessage = response.message || 'Failed to clone test';
          }
        },
        (error) => {
          console.error('Error cloning test:', error);
          this.errorMessage = 'Failed to clone test';
        }
      );
    }
  }

  confirmDelete() {
    if (this.test.assignment_count > 0) {
      alert('Cannot delete test with existing assignments');
      return;
    }

    if (confirm('Are you sure you want to delete this test? This action cannot be undone.')) {
      this.talentService.deleteCandidateTest(this.testId).subscribe(
        (response) => {
          if (response.status === 'success') {
            alert('Test deleted successfully');
            this.router.navigate(['/ai-test-list']);
          } else {
            this.errorMessage = response.message || 'Failed to delete test';
          }
        },
        (error) => {
          console.error('Error deleting test:', error);
          this.errorMessage = 'Failed to delete test';
        }
      );
    }
  }
}
